import { X } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { useWindowDimensions } from 'react-native';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';
import Reanimated, { useAnimatedStyle } from 'react-native-reanimated';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

interface KeyboardAwareActionsheetProps {
  isOpen: boolean;
  onClose: () => void;
  closeLabel: string;
  contentContainerClassName?: string;
  children: ReactNode;
}

/**
 * Native bottom sheet for a short prompt that holds a text field, with a close
 * button and backdrop dismissal.
 *
 * Built on Gluestack's `Actionsheet`. Do not move it onto `@gorhom/bottom-sheet`,
 * which the app no longer depends on: on the SDK 57 build gorhom's animated
 * reactions can fail to fire for a sheet mounted while the JS thread is busy.
 * The sheet then never slides in and its backdrop stays up as an invisible
 * full-screen layer that blocks every tap until the app is killed. A submitter
 * hit exactly that straight after submitting an inquiry.
 *
 * The Actionsheet does not avoid the keyboard, so a spacer at the end of the
 * scroll content grows by the keyboard height from
 * `react-native-keyboard-controller`. It reads the inset correctly under
 * Android edge-to-edge, where React Native's own `Keyboard` events report ~0.
 * The sheet sizes to its content, so the spacer lifts the field above the
 * keyboard on both platforms, and the height cap keeps a small phone with the
 * keyboard up scrollable rather than clipped.
 */
export function KeyboardAwareActionsheet({
  isOpen,
  onClose,
  closeLabel,
  contentContainerClassName = 'px-4 pb-6',
  children,
}: KeyboardAwareActionsheetProps) {
  const { height: windowHeight } = useWindowDimensions();

  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  const keyboardSpacerStyle = useAnimatedStyle(() => ({
    height: Math.max(0, -keyboardHeight.value),
  }));

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />

      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ maxHeight: Math.round(windowHeight * 0.92) }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <ActionsheetScrollView
          className="w-full"
          contentContainerClassName={contentContainerClassName}
          keyboardShouldPersistTaps="handled">
          <Pressable
            onPress={onClose}
            className="-mr-1 mb-1 self-end p-1"
            hitSlop={ICON_BUTTON_HIT_SLOP}
            {...getAccessibilityProps({ label: closeLabel, role: 'button' })}>
            <Icon as={X} size="md" className="text-typography-500" />
          </Pressable>

          {children}

          <Reanimated.View style={keyboardSpacerStyle} />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
